import React, { Suspense, useEffect } from 'react';
import { useQuery } from "react-query";
import * as apiClient from "../api-client";
// Import the CSS
import '../index.css';
import Loader from '../components/LoaderComponent';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import FAQLuxury from '../components/HolidayFAQs/LuxuryFaq';
import BlogSliderType from '../components/Blogs/BlogSliderType';
import HolidayTypeHotelsList from '../components/HolidayTypeHotelList';
import Slider1 from "../components/CountrySliders/Maldives/LuxurySlider";
import Slider2 from "../components/CountrySliders/Mauritius/LuxurySlider";
import HotelSliderSkeleton from '../components/Skeletons/HotelSliderSkeleton';
import ReadMoreLines from '../components/ReadMoreLines';

// Lazy load the components
// const LazyLatestDestinationCard = React.lazy(() => import('../components/LastestDestinationCard'));
const LazyWhyChoose = React.lazy(() => import('../components/WhyChoose/Luxury'));
const LazyVacationTypes = React.lazy(() => import('../components/Home/VacationTypes'));

const PageLuxury = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const { data: hotels } = useQuery("fetchAllInclusiveHotels", apiClient.fetchHotels);

  // const [isExpanded, setIsExpanded] = useState(false);
  // const toggleText = () => setIsExpanded(!isExpanded);

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>Luxury Holidays & Exclusive Escapes  | Halo Holidays</title>
          <meta name="description" content="Indulge in unparalleled luxury with Halo Holidays. Discover our curated collection of luxury resorts, exclusive experiences, and bespoke itineraries for an unforgettable escape." />
          {/* <meta name="keywords" content="travel, hotels, holidays, deals, holiday packages" /> */}
        </Helmet>
      </HelmetProvider>
      <section className="max-h-640 md:min-h-0 lg:min-h-0 my-8">
        <hr className="mb-10" />
        <h2 className="text-2xl font-semibold items-center justify-center flex mt-10">More reasons to choose us</h2>
        <Suspense fallback={<Loader />}>
          <LazyWhyChoose />
        </Suspense>
        <hr className="mb-10 mt-10" />
      </section>

      <section className="mx-4 my-4 md:mx-0 md:my-0">

        <div className='mt-2 mb-4'>
          <h1 className="text-2xl font-semibold">Luxury Holidays</h1>
          <ReadMoreLines mobileMaxRows={2} desktopMaxRows={2}>
            <p className="text-black md:mb-1 md:mt-2">
              Are you ready to elevate your travels to new heights of indulgence? Halo Holidays specializes in crafting extraordinary luxury holidays that cater to your every desire. From private island retreats and opulent resorts boasting infinity pools and private butlers to bespoke itineraries that immerse you in local culture and exclusive experiences that money can't buy, we'll transform your holiday dreams into reality.
              <br /><br />
              Immerse yourself in a world of unparalleled pampering, where every detail is meticulously planned and every moment is designed to delight. Relax in spacious suites with breathtaking views, whether it's overlooking the turquoise waters of the Maldives or the snow-capped peaks of the Alps. Savor gourmet cuisine prepared by world-renowned chefs, using only the freshest local ingredients and tantalizing your taste buds with every bite. Unwind with rejuvenating spa treatments that will leave you feeling refreshed, revitalized, and utterly pampered. Our luxury holidays are curated to offer the ultimate escape from the ordinary, allowing you to truly relax, recharge, and reconnect with your inner peace and with your loved ones.
              <br /><br />
              Whether you're seeking a <a href='https://www.haloholidays.co.uk/couple-holidays' className='text-halo-pink'>romantic getaway</a> to rekindle the spark, a family adventure filled with unique experiences, or a solo escape to pamper yourself and focus on well-being, our diverse collection of luxury resorts and destinations caters to every traveler's unique tastes and preferences. From the pristine beaches of the Maldives and the vibrant energy of Dubai to the cultural richness of Europe and the exotic allure of Southeast Asia, we'll help you find the perfect destination to match your desires. Let us turn your luxury holiday into a masterpiece of unforgettable moments and cherished memories.
            </p>
            <br />
            <h2 className='text-xl font-semibold'>Luxury Resorts: Your Gateway to Paradise</h2>
            <p className='text-black'>
              Experience the epitome of luxury at our handpicked collection of world-class resorts. From overwater bungalows in the Maldives with private decks for sunset cocktails to sprawling private villas in Bali boasting infinity pools and personal chefs, our luxury resorts redefine pampering and exclusivity. Unwind in lavish suites where every detail whispers elegance, from plush furnishings to breathtaking views of pristine beaches or snow-capped mountains. Savor exquisite culinary creations prepared by world-renowned chefs, using only the freshest local ingredients and tantalizing your taste buds with every bite.
              <br /><br />
              Rejuvenate your body and soul at award-winning spas, where expert therapists will pamper you with bespoke treatments and therapies. And for those who crave adventure, our bespoke itineraries offer unique experiences like private yacht charters, helicopter tours, and cultural immersions. For the spontaneous traveler, keep an eye on our exclusive last-minute deals, where you can snag incredible savings on luxury escapes to some of the world's most coveted destinations. Whether you're celebrating a special occasion or simply seeking a well-deserved escape, our luxury holidays are designed to exceed your every expectation and create memories that will last a lifetime.
            </p>
            <br />
            <h2 className='text-xl font-semibold'>More Than Just Luxury - Explore Diverse Holiday Styles</h2>
            <p className='text-black'>
              While luxury escapes are our specialty, Halo Holidays understands that every traveller has unique desires and interests. Seeking a fun-filled family holiday? We offer a wide range of exciting options that cater to all ages and preferences. Perhaps a group getaway with friends is more your style? Let us create a bespoke itinerary filled with adventure, laughter, and unforgettable moments. Or maybe you're yearning for a soul-searching solo adventure? We have a collection of inspiring journeys designed for personal growth and self-discovery.
              <br /><br />
              Dreaming of a spontaneous escape to paradise? Our last-minute <a href='https://www.haloholidays.co.uk/destinations/maldives' className='text-halo-pink'>Maldives holidays</a> offer the perfect blend of relaxation and adventure. Unwind on pristine beaches, explore vibrant coral reefs, and indulge in luxurious overwater bungalows – all at unbeatable prices.
              <br /><br />
              Whatever your travel style, Halo Holidays has the perfect escape to suit your needs. From family adventures and group getaways to solo journeys and last-minute deals, we're committed to creating unforgettable experiences that exceed your expectations.
            </p>
          </ReadMoreLines>
        </div>

        <Suspense fallback={<Loader />}>
          <section>
            <div className='mt-6 -mb-2 md:mt-0 md:mb-2'>
              <h2 className="text-xl font-semibold">Maldives Holiday Deals</h2>
            </div>
            <div className="hidden md:block lg:block lgx2:block">
              {hotels && (
                <HolidayTypeHotelsList hotels={hotels} holidayType="Luxury" holidayCountry="Maldives" />
              )}
            </div>
            <Suspense fallback={<HotelSliderSkeleton />}>
              <div className="container md:hidden lg:hidden">
                <Slider1 hotels={hotels ?? []} />
              </div>
            </Suspense>
          </section>
        </Suspense>

        <Suspense fallback={<Loader />}>
          <section>
            <div className='md:mt-4 -mb-2 md:mb-2'>
              <h2 className="text-xl font-semibold">Mauritius Holiday Deals</h2>
            </div>
            <div className="hidden md:block lg:block lgx2:block">
              {hotels && (
                <HolidayTypeHotelsList hotels={hotels} holidayType="Luxury" holidayCountry="Mauritius" />
              )}
            </div>
            <Suspense fallback={<HotelSliderSkeleton />}>
              <div className="container md:hidden lg:hidden">
                <Slider2 hotels={hotels ?? []} />
              </div>
            </Suspense>
          </section>
        </Suspense>

        <FAQLuxury />

        {/* <Suspense fallback={<Loader />}>
      <h2 className="text-3xl font-bold">Latest All Inclusive Destinations</h2>
      <p>Most recent all-inclusive destinations added by our hosts</p>
      </Suspense> */}
        {/* Displaying the top row hotels */}
        {/* <div className="grid md:grid-cols-2 grid-cols-1 gap-4">
        {topRowHotels.map((hotel) => (
          <Suspense fallback={<Loader />} key={hotel._id}>
            <LazyLatestDestinationCard hotel={hotel} />
          </Suspense>
        ))}
      </div>
       */}
        {/* Displaying the bottom row hotels */}
        {/* <div className="grid md:grid-cols-3 gap-4">
        {bottomRowHotels.map((hotel) => (
          <Suspense fallback={<Loader />} key={hotel._id}>
            <LazyLatestDestinationCard hotel={hotel} />
          </Suspense>
        ))}
      </div> */}

        <Suspense fallback={<Loader />}>
          <div className='p-4 md:p-0 md:mt-12 md:mb-12'>
            <LazyVacationTypes />
          </div>
        </Suspense>

        <section className='p-4 md:p-0'>
          <h2 className='text-2xl font-semibold mb-4'>Related Blogs</h2>
          <BlogSliderType blogType='all_inclusive' />
        </section>

        {/* <Suspense fallback={<Loader />}>
      <h2 className="text-3xl font-bold">More All Inclusive Destinations</h2>
      <p>Explore more all-inclusive destinations</p>
      </Suspense> */}

        {/* Optionally, display more hotels with AllInclusiveHotelCard */}
        {/* <div className="grid md:grid-cols-2 grid-cols-1 gap-4">
        {topRowHotels.map((hotel) => (
          <Suspense fallback={<Loader />} key={hotel._id}>
            <LazyAllInclusiveHotelCard hotel={hotel} />
          </Suspense>
        ))}
      </div> */}
      </section>
    </>
  );
};

export default PageLuxury;
